'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { CircularProgress, PulseButton, Search } from 'src/components';
import { StrategyTable, SortField, SortDirection } from '../StrategyTable';
import { StrategyDeleteModal } from '../StrategyDeleteModal';
import {
  useGetAllChainedStrategiesQuery,
  useDeleteChainedStrategyMutation,
} from 'src/api/redux';
import { useNotification } from 'src/context/notification';
import { useLogging } from 'src/context/logging';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { LOCATION } from 'src/hooks/filters/constants';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import { useDebounce } from 'use-debounce';

const ChainBuilderGrid = () => {
  const router = useRouter();
  const log = useLogging();
  const { setNotification } = useNotification();

  // Search state
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 300);

  // Sort state
  const [sortField, setSortField] = useState<SortField>('label');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  // Build query params for server-side sorting and searching
  const queryParams = {
    ...(debouncedSearch && { q: debouncedSearch }),
    sort: sortField,
    order: sortDirection,
  };

  const { data: chainedStrategies, isLoading } =
    useGetAllChainedStrategiesQuery(queryParams, {
      pollingInterval: 60_000,
    });

  const [deleteChainedStrategy, { isLoading: isDeleting }] =
    useDeleteChainedStrategyMutation();
  const { selectedStrategy, setSelectedStrategy } = useStrategies(
    LOCATION.STRATEGY_MANAGER
  );

  const [chainToDelete, setChainToDelete] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const handleSort = (field: SortField, direction: SortDirection) => {
    setSortField(field);
    setSortDirection(direction);
  };

  const handleAddChain = () => {
    log.event('Add chain button clicked', {
      source: 'ChainBuilderGrid',
      strategyMode: 'chain',
    });
    router.push('/settings/admin/strategy-manager/chain/add');
  };

  const handleEditChain = (id: string) => {
    const chain = chainedStrategies?.results?.find(
      (c) => c.id.toString() === id
    );

    log.event('Edit chain clicked', {
      source: 'ChainBuilderGrid',
      chainId: id,
      chainLabel: chain?.label,
      isChain: true,
    });

    router.push(`/settings/admin/strategy-manager/chain/${id}/edit`);
  };

  const handleCopyChain = (id: string) => {
    const chain = chainedStrategies?.results?.find(
      (c) => c.id.toString() === id
    );

    log.event('Copy chain clicked', {
      source: 'ChainBuilderGrid',
      chainId: id,
      chainLabel: chain?.label,
      isChain: true,
    });

    router.push(`/settings/admin/strategy-manager/chain/copy/${id}`);
  };

  const handleShowDialog = (chain) => {
    setChainToDelete(chain);
    setShowDeleteDialog(true);
  };

  const handleClose = () => {
    setChainToDelete(null);
    setShowDeleteDialog(false);
  };

  const handleDeleteChain = async (id: string) => {
    const chain = chainedStrategies?.results?.find(
      (c) => c.id.toString() === id
    );
    try {
      await deleteChainedStrategy(id);
      log.event('Strategy chain deleted', {
        source: 'ChainBuilderGrid',
        chainId: id,
        chainLabel: chain?.label,
        isChain: true,
      });
      setNotification({
        title: 'Chain deleted',
        message: 'Strategy chain deleted successfully',
        type: 'success',
      });
    } catch (err) {
      log.exception(`Error deleting strategy chain: ${err.data?.error_message}`);
      setNotification({
        title: 'Error deleting chain',
        message: err?.data?.error_message,
        type: 'error',
      });
    } finally {
      setShowDeleteDialog(false);
      setChainToDelete(null);
      if (selectedStrategy?.id?.toString() === id) {
        setSelectedStrategy(null);
      }
    }
  };

  const isAnyLoading = isLoading || isDeleting;

  return (
    <>
      <div className="relative space-y-4">
        {/* Add New Chain Button */}
        <div className="flex justify-between">
          <Search
            value={search}
            onSearch={setSearch}
            size="sm"
            placeholder="Search chains..."
            className="rounded-full min-w-64"
          />
          <PulseButton
            variant="solid"
            color="primary"
            size="sm"
            startDecorator={faPlus}
            onClick={handleAddChain}
          >
            New Chain
          </PulseButton>
        </div>

        {/* Chain Table */}
        <div
          className={`transition-all duration-200 ${
            isAnyLoading ? 'blur-sm pointer-events-none' : ''
          }`}
        >
          <StrategyTable
            strategies={(chainedStrategies?.results || []) as any[]}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
            onRowClick={(chain) => handleEditChain(chain.id.toString())}
            onEdit={(chain) => handleEditChain(chain.id.toString())}
            onDelete={(chain) => handleShowDialog(chain)}
            onCopy={(chain) => handleCopyChain(chain.id.toString())}
            hideTypeColumn
            showTeamsColumn
          />
        </div>

        {/* Loading overlay */}
        {isAnyLoading && (
          <div className="absolute inset-0 flex justify-center items-center bg-white/10 backdrop-blur-sm">
            <CircularProgress size="md" color="neutral" />
          </div>
        )}
      </div>

      <StrategyDeleteModal
        open={showDeleteDialog}
        onClose={handleClose}
        onDelete={handleDeleteChain}
        strategy={chainToDelete}
      />
    </>
  );
};

export default ChainBuilderGrid;
